import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { find, get, includes, isEmpty, map } from 'lodash';
import { Row, Col, Alert, Form, Button } from 'react-bootstrap';

import { GRAPH_STYLE_TYPES } from '../../../api/Graphics/Graphics';
import { SYMBOLOGY_TYPES } from '../../../api/Styles/Styles';

import ColorPicker from '../ColorPicker';
import SymbologyDataPreviewTable from '../SymbologyDataPreviewTable';

import ColorRangesField from '../../atoms/ColorRangesField';
import StyleOpacityField from '../../atoms/StyleOpacityField';

import {
  ENTRY_DATA_TYPES,
  X_AXIS_TEXT_ANGLE_VARIABLE,
  TEXT_SIZE_VARIBLE
} from '../../../modules/graphic-elements';

const GraphicLegendStep = ({
  graphicType,
  numericProperties,
  globalProperties,
  dataNumericType,
  changeStyleType,
  updateColor,
  updateAlpha,
  updateGlobalProperty,
  changeColorScale,
  changeCustomLegend,
  updateBorderColor,
  updateWeight,
  isMD,
  changeEditionStep
}) => {
  if (isEmpty(numericProperties)) {
    return <Alert variant="danger"> No numeric Property </Alert>;
  }
  const [property, changeProperty] = useState(numericProperties[0].property);
  const { styleData, styleType } = find(numericProperties, ['property', property]);
  const { color, alpha, borderColor, borderAlpha, colorRange, legend, weight } = styleData;
  const xAxisTextAngle = find(globalProperties, ['name', 'xAxisTextAngle']);
  const textSize = find(globalProperties, ['name', 'textSize']);

  return (
    <Row className="m-5">
      <Col xs={12} md={6} style={isMD ? { borderRight: '1px solid #ccc' } : {}}>
        {includes(
          [ENTRY_DATA_TYPES.MULTIPLEVALUES, ENTRY_DATA_TYPES.MULTIPLECOLORSANDVALUES],
          dataNumericType
        ) && (
          <Form.Group as={Row}>
            <Form.Control
              as="select"
              name="mapboxLayerName"
              value={property}
              onChange={event => changeProperty(event.target.value.trim())}
            >
              {map(numericProperties, ({ property: numericProperty }) => {
                return (
                  <option value={numericProperty} key={numericProperty}>
                    {numericProperty}
                  </option>
                );
              })}
            </Form.Control>
          </Form.Group>
        )}
        <Form.Group as={Row}>
          <Col xs={12}>
            <Form.Check
              type="radio"
              name="defaultColor"
              checked={styleType === GRAPH_STYLE_TYPES.SINGLE}
              value={GRAPH_STYLE_TYPES.SINGLE}
              label="Fixed Color"
              onChange={event => changeStyleType(event, property)}
            />
          </Col>
          <Form.Label column xs={{ span: 4, offset: 1 }}>
            Color
          </Form.Label>
          <Col xs={5} className="pt-2">
            <ColorPicker
              color={color}
              alpha={1}
              updateColor={colorStyle => updateColor(colorStyle, property)}
              disableAlpha
            />
          </Col>
          {!includes(
            [ENTRY_DATA_TYPES.SINGLECOLORSANDVALUES, ENTRY_DATA_TYPES.MULTIPLEVALUES],
            dataNumericType
          ) && (
            <>
              <Col xs={12} className="mt-2">
                <Form.Check
                  type="radio"
                  name="defaultColor"
                  checked={styleType === GRAPH_STYLE_TYPES.RANGED}
                  value={GRAPH_STYLE_TYPES.RANGED}
                  label="Color Range"
                  onChange={event => changeStyleType(event, property)}
                />
              </Col>
              <Col xs={{ span: 11, offset: 1 }}>
                <ColorRangesField
                  changeColorScale={event => changeColorScale(event, property)}
                  colorRange={colorRange}
                  symbologyType={SYMBOLOGY_TYPES.CATEGORIZED}
                  isCustom={false}
                  hasCustom={false}
                />
              </Col>
              <Col xs={12} className="mt-2">
                <Form.Check
                  type="radio"
                  name="defaultColor"
                  checked={styleType === GRAPH_STYLE_TYPES.CUSTOM}
                  value={GRAPH_STYLE_TYPES.CUSTOM}
                  label="Custom"
                  onChange={event => changeStyleType(event, property)}
                />
              </Col>
              <Col xs={{ span: 11, offset: 1 }}>
                <SymbologyDataPreviewTable
                  hasTitle={false}
                  symbologyType=""
                  legend={legend}
                  alpha={alpha}
                  changeCustomStatus={() => {}}
                  changeCustomLegend={(index, rgbColor) => {
                    changeCustomLegend(index, rgbColor, property);
                  }}
                />
              </Col>
            </>
          )}
        </Form.Group>
      </Col>
      <Col xs={12} md={6}>
        <Row>
          <Col xs={12} lg={6} className="mt-5">
            <Form.Group as={Row}>
              <Col xs={5} className="mt-2">
                <ColorPicker
                  color={borderColor}
                  alpha={borderAlpha}
                  updateColor={colorStyle => updateBorderColor(colorStyle, property)}
                />
              </Col>
              <Form.Label column xs={7}>
                Outline Color
              </Form.Label>
            </Form.Group>
          </Col>
          <Col xs={12} lg={6} className="mt-5">
            <Form.Group as={Row}>
              <Col xs={6}>
                <Form.Control
                  type="number"
                  name="weight"
                  value={weight}
                  min="0"
                  max="100"
                  onChange={event => updateWeight(event, property)}
                />
              </Col>
              <Form.Label column xs={6}>
                Outline Weight
              </Form.Label>
            </Form.Group>
          </Col>
        </Row>
        <Col xs={12} className="mt-5">
          <StyleOpacityField alpha={1} updateAlpha={event => updateAlpha(event, property)} />
        </Col>
        {includes(X_AXIS_TEXT_ANGLE_VARIABLE, graphicType) && (
          <Col xs={12} className="mt-5">
            <Form.Group>
              <Form.Label>X Axis Text Angle ({'\u00B0'}) </Form.Label>
              <Form.Control
                type="number"
                name="xAxisTextAngle"
                value={Number(get(xAxisTextAngle, 'value', '10'))}
                min="0"
                max="90"
                onChange={updateGlobalProperty}
              />
            </Form.Group>
          </Col>
        )}
        {includes(TEXT_SIZE_VARIBLE, graphicType) && (
          <Col xs={12} className="mt-5">
            <Form.Group>
              <Form.Label>Text Size</Form.Label>
              <Form.Control
                type="number"
                name="textSize"
                value={Number(get(textSize, 'value', '10'))}
                min="0"
                onChange={updateGlobalProperty}
              />
            </Form.Group>
          </Col>
        )}
        <Button variant="success" className="float-right" onClick={() => changeEditionStep(3)}>
          Save
        </Button>
      </Col>
    </Row>
  );
};
GraphicLegendStep.propTypes = {
  graphicType: PropTypes.string.isRequired,
  numericProperties: PropTypes.array.isRequired,
  globalProperties: PropTypes.array.isRequired,
  dataNumericType: PropTypes.string.isRequired,
  changeStyleType: PropTypes.func.isRequired,
  updateColor: PropTypes.func.isRequired,
  updateAlpha: PropTypes.func.isRequired,
  updateGlobalProperty: PropTypes.func.isRequired,
  changeColorScale: PropTypes.func.isRequired,
  changeCustomLegend: PropTypes.func.isRequired,
  updateBorderColor: PropTypes.func.isRequired,
  updateWeight: PropTypes.func.isRequired,
  isMD: PropTypes.bool.isRequired,
  changeEditionStep: PropTypes.func.isRequired
};

export default GraphicLegendStep;
